import React from "react";
import clsx from "clsx";
import { useSelector } from "react-redux";
import Message from "./Message";
import { SML_COLORS, TIMELINE_MESSAGES_LIMIT } from "Components/constants";
import useDelayedValue from "hooks/useDelayedValue";
import { getLog } from "Redux/selectors";
import styles from "./Log.module.css";

type LogProps = {
    logUid: string;
    replica?: boolean;
    delay: number;
    limit?: number;
    children?: React.ReactNode;
};

const Log = ({
    logUid,
    delay,
    replica = false,
    limit = TIMELINE_MESSAGES_LIMIT,
    children,
}: LogProps) => {
    const [animate, setAnimate] = React.useState(false);
    const log = useSelector((state) => getLog(state, logUid));
    const delayedLog = useDelayedValue(log, delay, log);

    const limitedLog = React.useMemo(
        () => [...delayedLog].reverse().splice(0, limit),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [delayedLog]
    );

    React.useEffect(() => {
        setAnimate(false);
        let raf = requestAnimationFrame(() => {
            raf = requestAnimationFrame(() => {
                setAnimate(true);
            });
        });

        return () => cancelAnimationFrame(raf);
    }, []);

    return (
        <div
            className={clsx(styles.root, { [styles.animate]: animate })}
            style={{
                transform: `translateX(calc(60px * ${delayedLog.length - 1}))`,
            }}
        >
            {children}
            <div
                className={styles.log}
                style={{
                    left: `calc(60px * ${
                        limitedLog.length - delayedLog.length
                    })`,
                }}
            >
                {limitedLog.map((message) => (
                    <Message
                        key={message.uid}
                        message={message}
                        replica={replica}
                        color={
                            SML_COLORS[message.partitionId % SML_COLORS.length]
                        }
                    />
                ))}
            </div>
        </div>
    );
};

export default Log;
